// src/dev/hooks/useNetworkLogger.ts
// Hook to intercept and log fetch calls for the inspector

import { useEffect, useRef } from "react";
import { useOnboardingInspector } from "../OnboardingInspectorContext";

// Check if URL is an onboarding-related Google Apps Script URL
function isOnboardingUrl(url: string): boolean {
  return url.includes("script.google.com/macros/s/");
}

// Extract tab name from URL query params
function extractTabFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get("tab");
  } catch {
    // URL might be relative or malformed
    const tabMatch = url.match(/[?&]tab=([^&]+)/);
    return tabMatch ? tabMatch[1] : null;
  }
}

/**
 * Hook to intercept fetch calls and log onboarding-related requests
 * Only active in development mode when inspector is open
 */
export function useNetworkLogger() {
  const { isOpen, addNetworkLog, updateNetworkLog } = useOnboardingInspector();
  const isDevMode = process.env.NODE_ENV === "development";
  const originalFetchRef = useRef<typeof fetch | null>(null);
  const activeLogsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    // Only install in dev mode
    if (!isDevMode) return;

    // Store original fetch
    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch;
    }

    const originalFetch = originalFetchRef.current;

    // Custom fetch wrapper
    const wrappedFetch = async (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> => {
      const url = typeof input === "string" ? input : input.toString();

      // Only intercept onboarding URLs when inspector is open
      if (!isOnboardingUrl(url) || !isOpen) {
        return originalFetch(input, init);
      }

      const tab = extractTabFromUrl(url);
      const startTime = performance.now();

      // Log the start of the request
      const logId = addNetworkLog({
        url,
        tab,
        status: "pending",
      });
      activeLogsRef.current.set(url, logId);

      try {
        const response = await originalFetch(input, init);
        const duration = performance.now() - startTime;

        // Clone response to read body without consuming it
        const clonedResponse = response.clone();

        try {
          const data = await clonedResponse.json();
          const rowCount = Array.isArray(data) ? data.length : undefined;
          const responsePreview = Array.isArray(data) && data.length > 0 ? data[0] : data;

          updateNetworkLog(logId, {
            status: response.ok ? "success" : "error",
            statusCode: response.status,
            duration,
            rowCount,
            responseSize: JSON.stringify(data).length,
            responsePreview,
          });
        } catch {
          // Response wasn't JSON
          updateNetworkLog(logId, {
            status: response.ok ? "success" : "error",
            statusCode: response.status,
            duration,
          });
        }

        return response;
      } catch (error) {
        const duration = performance.now() - startTime;
        updateNetworkLog(logId, {
          status: "error",
          duration,
          error: error instanceof Error ? error.message : "Unknown error",
        });
        throw error;
      }
    };

    // Replace global fetch
    window.fetch = wrappedFetch;

    // Cleanup: restore original fetch
    return () => {
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
      }
    };
  }, [isDevMode, isOpen, addNetworkLog, updateNetworkLog]);
}
